import React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import type { InterpolationValue } from 'styled-components';
import { graphql, useFragment } from 'react-relay';
import { Section } from '@atlaskit/side-navigation';
import { useIntl } from '@atlassian/jira-intl';
import type { starredFiltersList_navigationAppsSidebarCommon$key } from '@atlassian/jira-relay/src/__generated__/starredFiltersList_navigationAppsSidebarCommon.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { useFavoritesChangeEffect } from '../../../controllers/favourites-change-effect/index.tsx';
import { FilterItem } from '../filter-item/index.tsx';
import { MenuItem } from '../menu-item/main.tsx';
import { messages } from './messages.tsx';

type Props = {
	baseHref: string;
	jiraQuery: starredFiltersList_navigationAppsSidebarCommon$key;
	sectionCustomCss?: InterpolationValue[];
	onFavouritesChanged: () => void;
};

export const MAX_FAVORITE_FILTERS = 15;
const VIEW_ALL_STARRED_FILTERS_HREF = '/jira/filters?page=1&sortKey=favourite&sortOrder=DESC';

const analyticsPayload = {
	itemId: 'nin-view-all-starred-filters',
};

export const StarredFiltersList = ({
	baseHref,
	jiraQuery,
	onFavouritesChanged,
	sectionCustomCss,
}: Props) => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment starredFiltersList_navigationAppsSidebarCommon on JiraQuery
			@argumentDefinitions(
				cloudId: { type: "ID!" }
				isAddLimitToNav3StarredFilterSidebarEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-add-limit-to-nav3-starred-filter-sidebar-enabled.relayprovider"
				}
			) {
				favouriteFilters: favourites(
					cloudId: $cloudId
					filter: { type: FILTER, sort: { order: DESC } }
					first: 15
				) @include(if: $isAddLimitToNav3StarredFilterSidebarEnabled) {
					edges {
						node {
							... on JiraFilter {
								__typename
								filterId
								...filterItem_navigationAppsSidebarCommon
							}
						}
					}
					pageInfo {
						hasNextPage
					}
				}
				favouriteFiltersOld: favouriteFilters(cloudId: $cloudId)
					@skip(if: $isAddLimitToNav3StarredFilterSidebarEnabled) {
					edges {
						node {
							filterId
							...filterItem_navigationAppsSidebarCommon
						}
					}
				}
			}
		`,
		jiraQuery,
	);

	useFavoritesChangeEffect('filters', (hasPendingChanges, previousHasPendingChanges) => {
		// refresh when transitioning from pending to NOT pending
		if (previousHasPendingChanges && !hasPendingChanges) {
			onFavouritesChanged();
		}
	});

	const starredFilterEdges = fg('add_limit_to_nav3_starred_filter_sidebar')
		? data.favouriteFilters?.edges
				?.filter(Boolean)
				.filter((edge) => !!edge?.node?.filterId)
				.slice(0, MAX_FAVORITE_FILTERS) ?? []
		: data.favouriteFiltersOld?.edges || [];
	if (!starredFilterEdges.length) {
		return null;
	}

	return (
		<Section
			// TODO remove css prop once @atlaskit/section design gets updated
			// @ts-expect-error - TS2322: Index signature for type 'string' is missing in type 'StyledComponentClass<any, any, any>'.
			// eslint-disable-next-line @compiled/no-css-prop-without-css-function
			css={sectionCustomCss}
			title={formatMessage(messages.newStarredFiltersHeader)}
		>
			{starredFilterEdges.map((edge) =>
				edge?.node ? (
					<FilterItem
						key={edge.node.filterId}
						baseHref={baseHref}
						filter={edge.node}
						customAnalytics={{ isStarred: true }}
					/>
				) : null,
			)}
			{/* eslint-disable-next-line jira/ff/no-preconditioning */}
			{fg('add_limit_to_nav3_starred_filter_sidebar') &&
				(data.favouriteFilters?.pageInfo?.hasNextPage ||
					(data.favouriteFilters?.edges ?? []).length > MAX_FAVORITE_FILTERS) && (
					<MenuItem analytics={analyticsPayload} href={VIEW_ALL_STARRED_FILTERS_HREF}>
						{formatMessage(messages.viewAllStarredFilters)}
					</MenuItem>
				)}
		</Section>
	);
};
